<template>
  <div v-if="userHasPermissions('provider.contract.services.view')">
    <section-title content="Servicios en Contrato" has-line />

    <ProviderServiceManager
      v-if="showProviderServiceManager"
      ref="ProviderServiceManager"
      :duration-options="durations"
      :evaluation-type-options="evaluationTypes"
      :location-type-options="locationTypes"
      :modality-options="modalities"
      :municipality-options="municipalities"
      :school-type-options="schoolTypes"
      :service-options="serviceOptions"
      :service-type-options="serviceTypes"
      @add="handleAddService"
      @change-action="handleCahngeAction"
      @close="showProviderServiceManager = false"
      @remove="handleDeleteServices"
      @service-type-selected="updateServiceOptions"
      @toggle-bulk-action="toggleBulkAction"
      @toggle-search="showSearch = $event"
      @update="handleUpdateServices"
    />

    <separator
      v-if="showProviderServiceManager && showSearch"
      class="my-4"
      text="Búsqueda de servicios en contrato"
    />

    <div v-if="!showProviderServiceManager || showSearch" class="row">
      <div class="col-sm-12 col-md-3 mb-3">
        <custom-drop-down
          v-model="filterModel.serviceType"
          :clearable="true"
          fieldkey="id"
          fieldname="name"
          label="Tipo de Servicio:"
          :options="serviceTypes"
          placeholder="Seleccione"
        />
      </div>
      <div v-if="canShowEvaluationType" class="col-sm-12 col-md-3 mb-3">
        <custom-drop-down
          v-model="filterModel.evaluationType"
          :clearable="true"
          fieldkey="id"
          fieldname="name"
          label="Tipo de Evaluación:"
          :options="evaluationTypes"
          placeholder="Seleccione"
        />
      </div>
      <div class="col-sm-12 col-md-3 mb-3">
        <custom-drop-down
          v-model="filterModel.disciplineType"
          :clearable="true"
          fieldkey="id"
          fieldname="name"
          label="Servicio/Actividad:"
          :options="disciplineTypes"
          placeholder="Seleccione"
        />
      </div>
      <div v-if="canShowModality" class="col-sm-12 col-md-3 mb-3">
        <custom-drop-down
          v-model="filterModel.modality"
          :clearable="true"
          fieldkey="id"
          fieldname="name"
          label="Modalidad:"
          :options="modalities"
          placeholder="Seleccione"
        />
      </div>
      <div v-if="canShowDuration" class="col-sm-12 col-md-3 mb-3">
        <custom-drop-down
          v-model="filterModel.duration"
          :clearable="true"
          fieldkey="id"
          fieldname="name"
          label="Duración:"
          :options="filteredSearchDurations"
          placeholder="Seleccione"
        />
      </div>
      <div v-if="canShowLocationType" class="col-sm-12 col-md-3 mb-3">
        <custom-drop-down
          v-model="filterModel.locationType"
          :clearable="true"
          fieldkey="id"
          fieldname="name"
          label="Tipo de lugar:"
          :options="locationTypes"
          placeholder="Seleccione"
        />
      </div>
      <div v-if="canShowSchoolType" class="col-sm-12 col-md-3 mb-3">
        <custom-drop-down
          v-model="filterModel.schoolType"
          :clearable="true"
          fieldkey="id"
          fieldname="name"
          label="Tipo de Escuela:"
          :options="schoolTypes"
          placeholder="Seleccione"
        />
      </div>
      <div v-if="canShowMunicipality" class="col-sm-12 col-md-3 mb-3">
        <custom-drop-down
          v-model="filterModel.municipality"
          :clearable="true"
          fieldkey="id"
          fieldname="name"
          label="Municipio:"
          :options="municipalities"
          placeholder="Seleccione"
        />
      </div>

      <div
        class="col-sm-12 col-md-3 mb-3 d-flex align-items-end justify-content-between"
      >
        <action-button
          class="px-4"
          text="Buscar"
          variant="success"
          @click="onSearchHandler()"
        />

        <action-button
          v-if="
            (addEnabled && !showProviderServiceManager && !disabled) ||
              userHasPermissions('provider.contract.service.addupdate')
          "
          class="ml-4"
          i="fas fa-plus"
          text="Añadir/Actualizar Servicio"
          variant="outline-success"
          @click="showProviderServiceManager = true"
        />
      </div>
    </div>

    <CheckBox
      v-if="showProviderServiceManager"
      v-model="allSelected"
      class="select-all__container"
      :class="{
        'select-all-enable__container': !enabledBulkAction,
        'select-all-disable__container': enabledBulkAction,
      }"
      :disabled="!enabledBulkAction"
      text="Seleccionar Todo"
    />

    <div
      v-if="results != null && results.length > 0"
      class="service-information-list mt-3"
    >
      <custom-callout
        v-for="(service, idx) in contractServices"
        :key="idx"
        class="mb-2"
        :show-edition="
          !disabled &&
            editMode &&
            editInfo.data != null &&
            editInfo.index == idx
        "
      >
        <ProviderServiceResultRow
          v-model="contractServices[idx]"
          :delete-enabled="deleteEnabled"
          :disabled="disabled"
          :edit-enabled="editEnabled"
          :has-selection="enabledBulkAction"
          :idx="idx"
          @delete="onDelete"
          @edit="onEdit"
          @toggle="handleToggleService"
        />

        <template v-if="editMode && editInfo.index == idx" #edition>
          <service-information-add-edit
            v-model="editInfo.data"
            :custom-error-message="addEditCustomErrorMessage"
            @on-cancel="onServiceCanceled"
            @on-saved="onServiceSaved"
          />
        </template>
      </custom-callout>
      <div class="row">
        <div class="col-12">
          <pagination
            v-if="showPagination"
            :data="pagination"
            show-text
            @pagination-go-page="onSearchHandler($event)"
          />
        </div>
      </div>
    </div>
    <div v-else>
      <div class="row pt-2">
        <div class="col-12">
          <div class="col-12 pt-0 text-center">
            <em class="fal fa-folder-open icon fsi" />
            <span class="tsubhead-footer">No hay información disponible.</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CheckBox from 'CheckBox';
import SectionTitle from '@/shared/components/SectionTitle';
import ProviderServiceManager from './ProviderServiceManager';
import CustomCallout from '@/components/common/CustomCallout';
import Pagination from '@/components/common/Pagination';
import CustomDropDown from '@/components/common/form/CustomDropDown';
import ServiceInformationAddEdit from './ServiceInformationAddEdit';
import ProviderServiceResultRow from './ProviderServiceResultRow';

import DisciplineCategories from '@/utils/constants/disciplineCategories.js';
import ServiceTypes from '@/utils/constants/serviceTypes.js';
import Municipalities from '@/utils/constants/municipalities.js';
import EvaluationTypes from '@/utils/constants/evaluationTypes.js';
import DisciplineTypeCode from '@/utils/constants/disciplineTypeCode.js';
import BillingServiceTypes from '@/utils/constants/billingServiceTypes.js';
import Durations from '@/utils/constants/durations.js';

import {
  getContractServiceTypes as _getContractServiceTypes,
  getServices as _getServices,
  getServiceModalities as _getServiceModalities,
  searchContractServices as _searchContractServices,
  createContractService,
  updateContractServicesPrice,
  deleteContractServices,
} from '@/modules/provider/api';

import {
  getTherapyDurations as _getTherapyDurations,
  getTypePlaces as _getTypePlaces,
  getSchoolTypes as _getSchoolTypes,
  getEvaluationtypes as _getEvaluationtypes,
} from '@/modules/shared/code.api.js';

import { getMunicipalities as _getMunicipalities } from '@/modules/shared/location.api';

import alert from '@/mixins/alert.mixin';
import locationTypes from '@/utils/constants/locationTypes.js';

const DEFAULT_PAGE_SIZE = 10;

export default {
  name: 'ServiceInformationList',
  components: {
    CheckBox,
    CustomCallout,
    CustomDropDown,
    ServiceInformationAddEdit,
    SectionTitle,
    Pagination,
    ProviderServiceManager,
    ProviderServiceResultRow,
  },
  mixins: [alert],
  props: {
    contractId: {
      type: Number,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    onSaving: {
      type: Function,
      default: null,
    },
    onDeleting: {
      type: Function,
      default: null,
    },
    addEnabled: {
      type: Boolean,
      default: true,
    },
    editEnabled: {
      type: Boolean,
      default: true,
    },
    deleteEnabled: {
      type: Boolean,
      default: true,
    },
    companyId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      editInfo: {
        index: 0,
        data: null,
      },
      editMode: false,
      filterModel: {
        serviceType: null,
        disciplineType: null,
        evaluationType: null,
        modality: null,
        duration: null,
        locationType: null,
        schoolType: null,
        municipality: null,
      },
      pagination: {
        currentPage: 1,
        totalItemCount: 1,
        pageCount: 1,
        count: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      },
      results: [],
      activeFilters: {
        serviceTypeId: null,
        disciplineTypeId: null,
        locationTypeId: null,
        serviceModalityTypeId: null,
        therapyDurationId: null,
      },
      addEditCustomErrorMessage: null,
      disciplineTypes: [],
      serviceTypes: [],
      evaluationTypes: [],
      locationTypes: [],
      schoolTypes: [],
      modalities: [],
      durations: [],
      showProviderServiceManager: false,
      allSelected: false,
      enabledBulkAction: false,
      serviceOptions: [],
      selectedServiceIds: [],
      municipalities: [],
      showSearch: true,
    };
  },
  computed: {
    params() {
      return this.$route.params;
    },
    canShowEvaluationType() {
      return (
        this.filterModel.serviceType?.id == ServiceTypes.AdditionalEvaluation
      );
    },
    canShowModality() {
      return (
        this.filterModel.serviceType?.id == ServiceTypes.Therapy &&
        this.filterModel.disciplineType?.id != DisciplineTypeCode.Admision &&
        this.filterModel.disciplineType?.id !=
          DisciplineTypeCode.DiscusionDePlan
      );
    },
    canShowDuration() {
      return (
        this.filterModel.serviceType?.id == ServiceTypes.Consultancy ||
        (this.filterModel.serviceType?.id == ServiceTypes.Therapy &&
          this.filterModel.disciplineType?.id != DisciplineTypeCode.Admision &&
          this.filterModel.disciplineType?.id !=
            DisciplineTypeCode.DiscusionDePlan)
      );
    },
    canShowLocationType() {
      return (
        (this.filterModel.serviceType?.id == ServiceTypes.Therapy &&
          this.filterModel.disciplineType?.id != DisciplineTypeCode.Admision &&
          this.filterModel.disciplineType?.id !=
            DisciplineTypeCode.DiscusionDePlan) ||
        this.filterModel.serviceType?.id == ServiceTypes.AdditionalEvaluation ||
        this.filterModel.serviceType?.id == ServiceTypes.IntitialEvaluation
      );
    },
    canShowSchoolType() {
      return this.filterModel.locationType?.id === locationTypes.Escuela;
    },
    canShowMunicipality() {
      return (
        (this.filterModel.serviceType?.id == ServiceTypes.Therapy &&
          this.filterModel.disciplineType?.id != DisciplineTypeCode.Admision &&
          this.filterModel.disciplineType?.id !=
            DisciplineTypeCode.DiscusionDePlan) ||
        this.filterModel.serviceType?.id == ServiceTypes.AdditionalEvaluation ||
        this.filterModel.serviceType?.id == ServiceTypes.IntitialEvaluation
      );
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    contractServices() {
      return this.results.map((entity) => {
        let maped = {
          ...entity,
          selected: this.selectedServiceIds.includes(entity.id),
        };

        return maped;
      });
    },
    filteredSearchDurations() {
      const serviceTypes = [
        BillingServiceTypes.Terapia,
        BillingServiceTypes.Consultoria,
      ];

      if (!serviceTypes.includes(this.filterModel.serviceType?.id)) return [];

      const notRestrictedServices = [
        DisciplineTypeCode.ConsultoriaABA,
        DisciplineTypeCode.TerapiaEducativa,
      ];

      if (notRestrictedServices.includes(this.filterModel.disciplineType?.id)) {
        return this.durations;
      }

      const excludedDurations = [
        Durations.MIN240,
        Durations.MIN280,
        Durations.MIN300,
        Durations.MIN360,
        Durations.MIN420,
      ];
      return this.durations.filter(
        (option) => !excludedDurations.includes(option.id)
      );
    },
    companyIdValue() {
      return this.params.companyId || this.companyId;
    },
  },
  watch: {
    async 'filterModel.serviceType'(newValue) {
      this.disciplineTypes = [];
      this.filterModel.evaluationType = null;

      this.disciplineTypes = await this.getServiceOptions(newValue);
    },
    'filterModel.evaluationType'() {
      this.filterModel.disciplineType = null;
    },
    'filterModel.disciplineType'() {
      this.filterModel.modality = null;
      this.filterModel.duration = null;
      this.filterModel.locationType = null;
      this.filterModel.municipality = null;
    },
    allSelected(newValue) {
      if (!newValue) {
        this.selectedServiceIds = [];
        return;
      }

      this.selectedServiceIds = this.contractServices.map(
        (entity) => entity.id
      );
    },
  },
  async mounted() {
    await _getContractServiceTypes().then((response) => {
      this.serviceTypes = response.data;
    });
    await this.getDurations();
    await this.getModalities();
    await this.getLocationTypes();
    await this.getSchoolTypes();
    await this.getMunicipalities();
    await this.getEvaluationTypes();
    await this.onSearchHandler();
  },
  methods: {
    handleToggleService(payload) {
      if (payload.selected && !this.selectedServiceIds.includes(payload.id)) {
        this.selectedServiceIds = [...this.selectedServiceIds, ...[payload.id]];
        return;
      }

      if (!payload.selected && this.selectedServiceIds.includes(payload.id)) {
        this.selectedServiceIds = this.selectedServiceIds.filter(
          (id) => id != payload.id
        );
        return;
      }
    },
    toggleBulkAction(value) {
      this.enabledBulkAction = value;

      if (!value) {
        this.allSelected = false;
      }
    },
    async handleAddService(payload) {
      try {
        const isConfirmed = await this.ShowCancelModal(
          'Confirmación',
          `¿Está seguro(a) que desea editar el servicio ${payload.serviceName} del contrato?`,
          'Si',
          'No'
        );

        if (!isConfirmed) return;

        await createContractService(this.contractId, {
          serviceTypeId: payload.serviceTypeId,
          evaluationTypeId: payload.evaluationTypeId,
          disciplineTypeId: payload.serviceId,
          serviceModalityTypeId: payload.modalityId,
          therapyDurationId: payload.durationId,
          locationTypeId: payload.siteTypeId,
          schoolTypeId: payload.schoolTypeId,
          municipalityId: payload.municipalityId,
          cost: parseFloat(payload.price),
        });

        this.$refs['ProviderServiceManager'].clearForm();

        this.ShowToast(
          '',
          'El servicio ha sido añadido exitosamente.',
          'success'
        );
        this.filterModel = {
          serviceType: null,
          disciplineType: null,
          evaluationType: null,
          modality: null,
          duration: null,
          locationType: null,
          schoolType: null,
          municipality: null,
        };
        await this.onSearchHandler();
      } catch (error) {
        this.ShowToast('', error.response.data.message, 'error');
      }
    },
    async handleUpdateServices(data) {
      try {
        if (!this.selectedServiceIds.length) {
          this.ShowToast(
            '',
            'Favor seleccionar al menos un servicio.',
            'error'
          );
          return;
        }

        const selectedServiceNames = this.results
          .filter((service) => this.selectedServiceIds.includes(service.id))
          .map((selected) => selected.disciplineTypeName)
          .join(', ');

        const isConfirmed = await this.ShowCancelModal(
          'Confirmación',
          `¿Está seguro(a) que desea editar el servicio ${selectedServiceNames} del contrato?`,
          'Si',
          'No'
        );

        if (!isConfirmed) return;

        const payload = {
          contractId: parseInt(this.contractId),
          serviceIds: this.selectedServiceIds,
          price: parseFloat(data.price),
        };

        await updateContractServicesPrice(payload);

        this.$refs['ProviderServiceManager'].clearForm();

        this.ShowToast(
          '',
          'El servicio ha sido actualizado exitosamente.',
          'success'
        );

        await this.onSearchHandler();
      } catch (error) {
        this.ShowToast('', error.response.data.message, 'error');
      }
    },
    async handleDeleteServices() {
      try {
        if (!this.selectedServiceIds.length) {
          this.ShowToast(
            '',
            'Favor seleccionar al menos un servicio.',
            'error'
          );
          return;
        }

        const selectedServiceNames = this.results
          .filter((service) => this.selectedServiceIds.includes(service.id))
          .map((selected) => selected.disciplineTypeName)
          .join(', ');

        const isConfirmed = await this.ShowCancelModal(
          'Confirmación',
          `¿Está seguro(a) que desea eliminar el servicio ${selectedServiceNames} del contrato?`,
          'Si',
          'No'
        );

        if (!isConfirmed) return;

        const payload = {
          contractId: parseInt(this.contractId),
          serviceIds: this.selectedServiceIds,
        };
        await deleteContractServices(payload);

        this.ShowToast(
          '',
          'El servicio ha sido eliminado exitosamente.',
          'success'
        );

        await this.onSearchHandler();
        this.selectedServiceIds = [];
      } catch (error) {
        this.ShowToast('', error.response.data.message, 'error');
      }
    },
    async handleCahngeAction() {
      await this.onSearchHandler();
    },
    async getServiceOptions(serviceType) {
      if (!serviceType) {
        return [];
      }

      if (serviceType.id != ServiceTypes.Consultancy) {
        const { data } = await _getServices(
          [serviceType.id],
          [
            DisciplineCategories.ValidForEncuentro,
            DisciplineCategories.ValidForAppointment,
          ]
        );

        return data;
      }

      const { data } = await _getServices([serviceType.id]);
      return data;
    },
    async updateServiceOptions(newValue) {
      this.serviceOptions = await this.getServiceOptions(newValue);
    },
    async onSearchHandler(page = 1) {
      this.pagination.currentPage = page;
      const payload = {
        contractId: this.contractId,
        companyId: this.companyIdValue,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
        serviceTypeId: this.filterModel.serviceType?.id,
        disciplineTypeId: this.filterModel.disciplineType?.id,
        evaluationTypeId: this.filterModel.evaluationType?.id,
        modalityId: this.filterModel.modality?.id,
        durationId: this.filterModel.duration?.id,
        locationTypeId: this.filterModel.locationType?.id,
        schoolTypeId: this.filterModel.schoolType?.id,
        municipalityId: this.filterModel.municipality?.id,
      };

      const { data: results } = await _searchContractServices(payload);
      this.results = results.data;

      this.pagination = {
        ...this.pagination,
        totalItemCount: results.totalItemCount,
        pageCount: results.pageCount,
        currentPage: results.currentPage,
        count: results.count,
      };
      this.allSelected = false;
      this.selectedServiceIds = [];
    },
    onEdit(dataItemIndex) {
      this.editMode = true;

      this.editInfo = {
        index: dataItemIndex,
        data: this.results[dataItemIndex],
      };
    },
    async onDelete(dataItemIndex) {
      await this.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea eliminar el servicio seleccionado?',
        'Sí',
        'No'
      ).then(async (answer) => {
        if (answer) {
          let isValid = true;

          if (this.onDeleting != null)
            isValid = await this.onDeleting(this.results[dataItemIndex].id);

          if (isValid) {
            var modelIndex = this.results.findIndex(
              (x) => x.id == this.results[dataItemIndex].id
            );

            this.results.splice(modelIndex, 1);
            this.ShowToast(
              'Proceso exitoso',
              'El servicio ha sido eliminado exitosamente.',
              'success'
            );
          }
        }
      });
    },
    async onServiceSaved(savedService) {
      let isValid = true;
      //Check for duplicates )only for new records)
      if (savedService.id == null) {
        isValid = !this.results.some(
          (x) =>
            x.serviceTypeId == savedService.serviceTypeId &&
            x.disciplineTypeId == savedService.disciplineTypeId &&
            x.serviceModalityTypeId == savedService.serviceModalityTypeId &&
            x.locationTypeId == savedService.locationTypeId &&
            x.schoolTypeId == savedService.schoolTypeId &&
            x.therapyDurationId == savedService.therapyDurationId
        );

        if (!isValid && !this.editMode) {
          //TODO: Use the stored messages
          this.addEditCustomErrorMessage =
            'Se identificó que el servicio ya existe en el contrato.';
          this.ShowToast('Error', this.addEditCustomErrorMessage, 'error');
          return;
        } else {
          //Clear the message
          this.addEditCustomErrorMessage = null;
        }
      }

      if (this.onSaving != null) {
        isValid = await this.onSaving(savedService);
      }

      if (isValid) {
        if (savedService.isNew) {
          savedService.isNew = false;
          this.results.unshift(savedService);
        } else {
          var foundIndex = this.results.findIndex(
            (x) => x.id == savedService.id
          );
          var modelIndex = this.results.findIndex(
            (x) => x.id == this.results[foundIndex].id
          );

          this.results[modelIndex] = savedService;
        }

        this.clearEditCreate();
      }
    },
    async onServiceCanceled() {
      this.clearEditCreate();
    },
    clearEditCreate() {
      this.addEditCustomErrorMessage = null;
      this.editMode = false;

      this.editInfo = {
        index: 0,
        data: null,
      };
    },
    async getDurations() {
      this.durations = (await _getTherapyDurations()).data;
    },
    async getModalities() {
      const { data } = await _getServiceModalities({ forTh: true });

      this.modalities = data;
    },
    async getLocationTypes() {
      this.locationTypes = (await _getTypePlaces()).data;
    },
    async getSchoolTypes() {
      this.schoolTypes = (await _getSchoolTypes()).data;
    },
    async getMunicipalities() {
      const { data } = await _getMunicipalities({ academicYearId: 0 });

      this.municipalities = data.filter(
        (x) => x.id == Municipalities.Culebra || x.id == Municipalities.Vieques
      );
    },
    async getEvaluationTypes() {
      let data = (await _getEvaluationtypes()).data;

      this.evaluationTypes = data.filter(
        (x) =>
          x.id == EvaluationTypes.Evaluación_Adicional ||
          x.id == EvaluationTypes.Evaluación_Trianual ||
          x.id == EvaluationTypes.Reevaluacion
      );
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/shared/scss/config/fonts.scss';
@import '~@/shared/scss/config/colors.scss';

.tsubhead-footer {
  font-size: 14px;
  color: #cdcdcd;
}
.fsi {
  font-size: 36px;
  color: #cdcdcd;
}

.select-all__container {
  display: flex;
  justify-content: end;
  margin: 20px 0;
}

::v-deep .select-all-enable__container label {
  color: #c4c4c4;
  text-decoration-line: none;
}

::v-deep .select-all-disable__container label {
  color: #3dbc8c;
  text-decoration-line: underline;
}
</style>
